import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Allows safe tags like spans, strongs, etc. for formatting.
 */
export function sanitizeHtml(html) {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['span', 'strong', 'em', 'b', 'i', 'br', 'p', 'div'],
    ALLOWED_ATTR: ['class', 'data-donor', 'data-list', 'data-group', 'style'],
    // Strip dangerous protocols
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/**
 * Escape HTML entities in text (for when you don't want any HTML).
 * Use this before adding your own HTML tags.
 */
export function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Sanitize and highlight text with clickable names.
 * First escapes the input, then adds safe HTML spans.
 */
export function highlightWithNames(text, donorNames = [], listNames = [], groupNames = []) {
  if (!text) return '';

  // First escape the raw text
  let result = escapeHtml(text);

  // Convert markdown bold to HTML
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Make donor names clickable (sort by length to match longer names first)
  const sortedDonorNames = [...donorNames].sort((a, b) => b.length - a.length);
  for (const name of sortedDonorNames) {
    if (name.length < 3) continue;
    const escapedName = escapeHtml(name);
    const namePattern = new RegExp(`\\b(${escapedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    result = result.replace(namePattern, `<span class="clickable-name donor-link" data-donor="$1">$1</span>`);
  }

  // Make list names clickable
  const sortedListNames = [...listNames].sort((a, b) => b.length - a.length);
  for (const name of sortedListNames) {
    if (name.length < 3) continue;
    const escapedName = escapeHtml(name);
    const namePattern = new RegExp(`\\b(${escapedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    result = result.replace(namePattern, `<span class="clickable-name list-link" data-list="$1">$1</span>`);
  }

  // Make group names clickable
  const sortedGroupNames = [...groupNames].sort((a, b) => b.length - a.length);
  for (const name of sortedGroupNames) {
    if (name.length < 3) continue;
    const escapedName = escapeHtml(name);
    const namePattern = new RegExp(`\\b(${escapedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    result = result.replace(namePattern, `<span class="clickable-name group-link" data-group="$1">$1</span>`);
  }

  // Final sanitization pass to ensure output is safe
  return sanitizeHtml(result);
}

/**
 * Highlight money amounts and meeting words in text.
 * First escapes input, then adds safe spans.
 */
export function highlightMoneyAndMeetings(text) {
  if (!text) return '';

  // Escape the raw text and sanitize
  return sanitizeHtml(escapeHtml(text));
}
